<template>
  <b-container fluid>
    <backlink />
    <h1>Facturation automatique</h1>

    <AdminTable ref="table" :config="config" :options="options" :items_provider="itemsProvider">
      <template slot="top-buttons">
        <b-button
          class="float-right mb-35"
          variant="outline-primary"
          size="lg"
          @click="openGenerationModal()"
        >
          Générer des lignes de facturation
          <i class="far fa-plus ml-10"></i>
        </b-button>
      </template>
      <template slot="actions" slot-scope="{ data }">
        <!-- Une facturation n'est exportable qu'une fois générée par le worker -->
        <b-button
          @click.prevent="openExportCsvModal(data.item.id)"
          :disabled="!isGenerated(data.item)"
          :title="isGenerated(data.item) ? 'Exporter en CSV' : 'Export disponible une fois la génération terminée'"
          variant="outline-secondary"
        >
          <i v-if="['pending', 'running'].includes(data.item.generation_status)" class="far fa-hourglass-half"></i>
          <i v-else class="far fa-file-csv"></i>
        </b-button>
        <!-- Génération en échec : on la relance sur la même période (même facturation côté API) -->
        <b-button
          v-if="data.item.generation_status === 'failed'"
          @click.prevent="openGenerationModal(data.item)"
          :title="'Relancer la génération' + (data.item.generation_error ? ' — ' + data.item.generation_error : '')"
          variant="outline-warning"
        >
          <i class="far fa-redo"></i>
        </b-button>
        <!-- Mise au point, en local uniquement : supprime la facturation et ce que sa
             génération a produit, pour la relancer. Jamais sur une facturation reprise de MCS. -->
        <b-button
          v-if="canResetBilling && !data.item.from_mcs"
          @click.prevent="openResetModal(data.item)"
          :disabled="resettingId === data.item.id"
          title="Supprimer cette facturation (local uniquement)"
          variant="outline-danger"
        >
          <i :class="resettingId === data.item.id ? 'far fa-spinner fa-spin' : 'far fa-trash-alt'"></i>
        </b-button>
      </template>
    </AdminTable>

    <!-- Suppression d'une facturation générée : environnement dev uniquement -->
    <b-modal id="billing_reset_modal" centered hide-footer no-close-on-backdrop>
      <template slot="modal-title">
        <i class="far fa-trash-alt"></i>
        Supprimer la facturation
      </template>
      <div v-if="resetItem">
        <b-alert show variant="warning">
          <i class="far fa-flask"></i>
          Outil de mise au point, <strong>uniquement disponible en environnement dev</strong>.
        </b-alert>
        <p>
          La facturation du <strong>{{ resetItem.start_date }}</strong> au
          <strong>{{ resetItem.end_date }}</strong> sera supprimée, avec tout ce que sa
          génération a produit :
        </p>
        <ul>
          <li>ses lignes de location ;</li>
          <li>les factures « Location pour compte » et « Frais de location pour compte » ;</li>
          <li>les frais à facturer repassent « À facturer » ;</li>
          <li>les autres factures manuelles de la période sont détachées.</li>
        </ul>
        <p class="mb-0">Il suffira ensuite de relancer la génération du mois.</p>
      </div>
      <div class="d-flex justify-content-end mt-4">
        <b-button variant="outline-secondary" class="mr-2" :disabled="resettingId !== null" @click="closeResetModal()">
          Annuler
        </b-button>
        <b-button class="btn-reset-billing" :disabled="resettingId !== null" @click="resetBilling()">
          <i :class="resettingId !== null ? 'far fa-spinner fa-spin' : 'far fa-trash-alt'"></i>
          Supprimer
        </b-button>
      </div>
    </b-modal>

    <b-modal
      id="billing_lines_generation_modal"
      lazy
      hide-footer
      centered
      no-close-on-backdrop
      size="lg"
    >
      <template slot="modal-title">
        <i class="far fa-euro-sign"></i>
        Générer des lignes de facturation
      </template>
      <b-form inline class="align-items-end" @submit.prevent="generateLines">
        <b-form-group label="Année" label-for="year">
          <b-form-select id="year" v-model="year" :options="year_options" :required="true">
            <template slot="first">
              <option :value="null">-- Sélectionnez une option --</option>
            </template>
          </b-form-select>
        </b-form-group>
        <b-form-group label="Mois" label-for="month">
          <b-form-select id="month" v-model="month" :options="month_options" :required="true">
            <template slot="first">
              <option :value="null">-- Sélectionnez une option --</option>
            </template>
          </b-form-select>
        </b-form-group>
        <b-button type="submit" variant="primary" :disabled="inGenerating" class="ml-auto">
          {{ inGenerating ? "Génération..." : "Générer pour la période" }}
          <i v-if="!inGenerating" class="fa fa-chevron-right"></i>
        </b-button>
      </b-form>

      <div class="ibox mt-5 ml-0 mr-0" v-if="nb_bill_lines">
        <div class="ibox-content">
          <h2>Synthèse des lignes générées</h2>
          <dl>
            <dt>Du :</dt>
            <dd class="float-left">{{ start_date }}</dd>
            <dt class="ml-2" style="clear: none">Au :</dt>
            <dd>{{ end_date }}</dd>
            <dt>Nombre de lignes de facturation :</dt>
            <dd>{{ formatCount(nb_bill_lines) }}</dd>
            <dt>Total des factures manuelles :</dt>
            <dd>{{ formatAmount(total_billing) }}</dd>
            <dt>Total des locations :</dt>
            <dd>{{ formatAmount(total_renting) }}</dd>
          </dl>
        </div>
      </div>
      <!-- Génération en tâche de fond (worker Messenger) : on peut fermer la fenêtre, elle se
           poursuit sur le serveur et la liste en affiche le statut. -->
      <ExportProgress
        v-if="generationSteps.length"
        :steps="generationSteps"
        :running="generationRunning"
        :elapsed-label="generationElapsedLabel"
        :percent="generationPercent"
        done-label="Facturation générée en"
      />
      <p v-if="generationRunning" class="text-muted">
        La génération se poursuit sur le serveur : vous pouvez fermer cette fenêtre.
      </p>
      <div v-if="nb_bill_lines && !generationId" class="form-submit">
        <b-button type="submit" variant="primary" :disabled="inValidating" @click.prevent="validateGeneration">
          {{ inValidating ? "Enregistrement..." : "Valider" }}
          <i v-if="!inValidating" class="fa fa-chevron-right"></i>
        </b-button>
      </div>
    </b-modal>

    <b-modal id="billing_lines_export_modal" lazy hide-footer centered no-close-on-backdrop>
      <template slot="modal-title">
        <i class="far fa-download"></i>
        Export des lignes de facturation
      </template>
      <p class="export-intro">Choisissez le format du fichier à télécharger.</p>

      <!-- Cartes natives plutôt que b-form-radio : en bootstrap-vue rc.13 le composant rend
           un <label> avec un <input> en position absolue, qui déborde de sa carte dès qu'on
           la met en display:block — un clic sélectionnait alors le format voisin. -->
      <div class="export-formats" role="radiogroup" aria-label="Format du fichier">
        <button
          v-for="format in exportFormats"
          :key="format.value"
          type="button"
          role="radio"
          :aria-checked="exportFormat === format.value"
          class="export-format"
          :class="{ 'is-selected': exportFormat === format.value }"
          @click="exportFormat = format.value"
        >
          <span class="export-format__radio"></span>
          <span class="export-format__text">
            <span class="export-format__name">{{ format.name }}</span>
            <span class="export-format__desc">{{ format.desc }}</span>
          </span>
        </button>
      </div>

      <!-- Mise au point du mapping SAP : hors production, on peut ne sortir que les lignes
           venues de « Frais à facturer ». Elles se comptent sur les doigts d'une main au
           milieu de plusieurs milliers de lignes de location, et c'est le seul moyen de les
           relire. Le serveur ignore le paramètre hors `dev` : ce fichier partiel porte le nom
           de l'export SAP, il n'a rien à faire chez l'ADV. -->
      <label v-if="canFilterBillableFees" class="export-only-fees"
        :class="{ 'is-active': exportOnlyBillableFees }">
        <input type="checkbox" v-model="exportOnlyBillableFees" />
        <span class="export-only-fees__text">
          <span class="export-only-fees__name">
            Uniquement les frais à facturer
            <span class="export-only-fees__badge">DEV</span>
          </span>
          <small>Locations et factures manuelles ordinaires écartées</small>
        </span>
      </label>

      <!-- Le fichier est produit par le worker Messenger (extraction, cf. mixin dataExport) -->
      <ExportProgress :steps="exportSteps" :running="exportLoading" :elapsed-label="exportElapsedLabel"
        :percent="exportPercent" />

      <div class="form-submit">
        <b-button v-if="!exportFormat" disabled type="submit" variant="primary">
          Sélectionnez un format
        </b-button>
        <template v-else-if="exportLoading">
          <b-button v-if="exportId" variant="outline-secondary" class="mr-2" :disabled="cancelling" @click="cancelExport">
            <i class="fa fa-times"></i>
            {{ cancelling ? "Annulation..." : "Annuler" }}
          </b-button>
          <b-button disabled type="submit" variant="primary">
            <i class="fa fa-spinner fa-spin"></i>
            Génération du fichier&hellip;
          </b-button>
        </template>
        <!-- Une période sans encours est une réponse valide pour « Charges à payer » : on le
             dit, plutôt que de proposer un fichier vide. -->
        <b-button v-else-if="exportReady && !exportRowCount" disabled type="submit" variant="primary">
          Aucune ligne à exporter
        </b-button>
        <b-button v-else-if="exportReady" variant="primary" :disabled="downloading" @click="downloadExport">
          <i class="far fa-file-csv"></i>
          Télécharger &mdash; {{ formatCount(exportRowCount) }} lignes
        </b-button>
        <!-- Échec ou annulation : le fichier se relance sans rechoisir le format -->
        <b-button v-else variant="primary" @click="loadExportRows">
          <i class="far fa-redo"></i>
          Relancer l'export
        </b-button>
      </div>
    </b-modal>
  </b-container>
</template>

<script>
import AdminTable from "@/components/Global/AdminTableSync";
import ExportProgress from "@/components/Global/ExportProgress";
import dataExportMixin from "@/mixins/dataExport";
import config from "./config";

// Sondage de l'avancement de la génération (ms)
const GENERATION_POLL_INTERVAL = 1500;
// Rafraîchissement de la liste tant qu'une génération y est en cours (ms)
const LIST_REFRESH_INTERVAL = 5000;

export default {
  name: "AutomaticBillingList",
  components: {
    AdminTable,
    ExportProgress,
  },
  // Export CSV par le worker Messenger : création, suivi (étapes, chronomètre), téléchargement
  mixins: [dataExportMixin],
  data() {
    return {
      config: config,
      year_options: [],
      month_options: [],
      year: parseInt(this.$moment().subtract(1, "month").format("YYYY")),
      month: parseInt(this.$moment().subtract(1, "month").format("M")),
      inGenerating: false,
      generate_lines_fields: [],
      canResetBilling: process.env.NODE_ENV === "development",
      resettingId: null,
      resetItem: null,
      internalBillingLines: [],
      generated_lines: [],
      inValidating: false,
      start_date: null,
      end_date: null,
      nb_bill_lines: null,
      total_billing: null,
      total_renting: null,
      // Génération en tâche de fond : facturation suivie, étapes et chronomètre
      generationId: null,
      generationSteps: [],
      generationRunning: false,
      generationPercent: null,
      generationElapsed: 0,
      generationClock: null,
      generationPollTimer: null,
      listRefreshTimer: null,
      // Aucun format présélectionné : le choix est explicite, et rien n'est chargé tant
      // qu'aucun format n'est retenu.
      exportFormat: null,
      exportFormats: [
        {
          value: "legacy",
          name: "Format Axiloc",
          desc: "Format historique — 14 colonnes",
        },
        {
          value: "sap",
          name: "Format SAP",
          desc: "Pour intégration dans SAP — 34 colonnes + statut de l'affaire + 6 colonnes de contrôle (entité, affaire, matériel, tarif, utilisateur, demandeur)",
        },
        {
          value: "charges",
          name: "Charges à payer",
          desc: "Encours locatif arrêté en fin de période — 14 colonnes : le modèle client complété de la société, du responsable d'affaire et du reste à engager",
        },
      ],
      exportedBillingId: null,
      // Filtre de mise au point (format SAP, hors production) : n'exporter que les lignes
      // issues de l'écran « Frais à facturer ».
      exportOnlyBillableFees: false,
    };
  },
  watch: {
    exportFormat() {
      this.loadExportRows();
    },
    exportOnlyBillableFees() {
      this.loadExportRows();
    },
  },
  computed: {
    // Le filtre n'a de sens que sur le format SAP, et n'existe pas en production : le serveur
    // ignore le paramètre ailleurs, la case n'a donc pas à s'y montrer.
    canFilterBillableFees: function () {
      return this.exportFormat === "sap" && process.env.NODE_ENV !== "production";
    },
    generationElapsedLabel() {
      return (this.generationElapsed / 1000).toFixed(1).replace(".", ",") + " s";
    },
    options: function () {
      return {
        agency_options: this.$store.getters["agency/getAll"].selectOptions("id", [
          "internal_number",
          " - ",
          "name",
        ]),
        from_mcs_options: [
          { value: null, text: "Toutes" },
          { value: "1", text: "Import MCS" },
          { value: "0", text: "Catalogue ESM" },
        ],
      };
    },
  },
  created() {
    let year_options = [];
    for (let year = 2019; year <= this.$moment().format("YYYY"); year++) {
      year_options.push({
        value: year,
        text: year,
      });
    }
    this.year_options = year_options;

    let month_options = [];
    const months = this.$moment.months();
    for (let month = 1; month <= 12; month++) {
      month_options.push({
        value: month,
        text: months[month - 1].capitalize(),
      });
    }
    this.month_options = month_options;

    // Chargement des champs à afficher dans la liste de la modale de génération
    this.generate_lines_fields = config.fields
      .filter((field) => field.inList == true)
      .map((field) => ({
        key: field.name,
        label: field.label,
        sortable: field.sortable || false,
      }));

    this.$store.dispatch("agency/getAllForSelect");
  },
  mounted() {
    // La modale se ferme aussi par la croix ou Échap : on passe par l'événement global
    // plutôt que par le bouton, pour retirer le paramètre dans tous les cas.
    this.$root.$on("bv::modal::hidden", this.onModalHidden);

    // Réouverture de l'export si un id est présent dans l'URL (rafraîchissement, lien
    // partagé). En `$nextTick` : la modale n'est montée qu'après le rendu de la liste.
    this.$nextTick(() => {
      const exportId = this.$route.query.export;
      if (exportId) {
        this.openExportCsvModal(parseInt(exportId));
      }
    });
  },
  beforeDestroy() {
    this.$root.$off("bv::modal::hidden", this.onModalHidden);
    this.stopGenerationTracking();
    clearTimeout(this.listRefreshTimer);
  },
  methods: {
    /**
     * Fournit les données à AdminTableSync (pagination serveur). Appelé à chaque
     * changement de page / tri / rafraîchissement.
     * @param   {object}  ctx   Contexte envoyé par b-table
     * @returns {Promise}       { total, page, items }
     */
    itemsProvider(ctx) {
      return this.$http
        .get(process.env.BACK_API_URL + "internalbilling", {
          params: {
            page: ctx.currentPage,
            limit: ctx.perPage,
            sortBy: ctx.sortBy,
            sortDesc: ctx.sortDesc ? "DESC" : "ASC",
            from_mcs: ctx.extended_filters_model.from_mcs,
            start_date: ctx.extended_filters_model.period.start
              ? this.$moment(ctx.extended_filters_model.period.start).format("YYYY-MM-DD")
              : null,
            end_date: ctx.extended_filters_model.period.end
              ? this.$moment(ctx.extended_filters_model.period.end).format("YYYY-MM-DD")
              : null,
          },
        })
        .then((response) => {
          const items = response.data.internalBillings || [];
          // Une génération en cours : la liste se rafraîchit jusqu'à ce qu'elle aboutisse
          clearTimeout(this.listRefreshTimer);
          if (items.some((item) => ["pending", "running"].includes(item.generation_status))) {
            this.listRefreshTimer = setTimeout(() => this.$refs.table && this.$refs.table.refresh(), LIST_REFRESH_INTERVAL);
          }
          return {
            total: response.data.total,
            page: parseInt(response.data.page),
            items: items,
          };
        })
        .catch(() => {
          this.$toastr.error("Le chargement de la liste a échoué");
          return { total: 0, items: [] };
        });
    },
    // Synthèse de génération : même présentation que les colonnes de la liste (config.js)
    formatCount(value) {
      return value || value === 0 ? Number(value).toLocaleString("fr-FR") : "-";
    },
    formatAmount(value) {
      return value || value === 0
        ? parseFloat(value).toLocaleString("fr-FR", { minimumFractionDigits: 2, maximumFractionDigits: 2 }) + " €"
        : "-";
    },
    openResetModal(item) {
      this.resetItem = item;
      this.$root.$emit("bv::show::modal", "billing_reset_modal");
    },
    closeResetModal() {
      this.$root.$emit("bv::hide::modal", "billing_reset_modal");
    },
    /**
     * Supprime la facturation choisie dans la modale (outil local, cf. InternalBillingReset côté API).
     */
    resetBilling() {
      const item = this.resetItem;
      this.resettingId = item.id;
      this.$http
        .delete(process.env.BACK_API_URL + "internalbilling/" + item.id + "/reset")
        .then((response) => {
          this.$toastr.success(
            "Facturation supprimée : " +
              response.data.lines +
              " lignes, " +
              response.data.generated_manual_billings +
              " factures de location pour compte, " +
              response.data.fees +
              " frais remis à facturer"
          );
          this.closeResetModal();
          this.$refs.table.refresh();
        })
        .catch(() => {
          this.$toastr.error("La suppression de la facturation a échoué");
        })
        .finally(() => {
          this.resettingId = null;
        });
    },
    /** Facturation exportable : générée (les lignes reprises de MCS n'ont pas de statut). */
    isGenerated(item) {
      return !item.generation_status || item.generation_status === "done";
    },
    /**
     * Ouvre la modale de génération, sur la période d'une facturation en échec pour la relancer.
     */
    openGenerationModal(item) {
      this.stopGenerationTracking();
      this.generationId = null;
      this.generationSteps = [];
      this.nb_bill_lines = null;
      if (item) {
        const start = this.$moment(item.start_date);
        this.year = parseInt(start.format("YYYY"));
        this.month = parseInt(start.format("M"));
      }
      this.$root.$emit("bv::show::modal", "billing_lines_generation_modal");
    },
    generateLines() {
      this.inGenerating = true;
      this.$http
        .get(process.env.BACK_API_URL + "internalbillingline/period/total", {
          params: {
            year: this.year,
            month: this.month,
          },
        })
        .then((response) => {
          this.start_date = response.data.start_date;
          this.end_date = response.data.end_date;
          this.nb_bill_lines = response.data.nb_bill_lines;
          this.total_billing = response.data.total_billing;
          this.total_renting = response.data.total_renting;
          this.inGenerating = false;
          // Nouvel aperçu : le suivi d'une génération terminée laisse la place à la validation
          if (!this.generationRunning) {
            this.generationId = null;
            this.generationSteps = [];
          }
        })
        .catch((error) => {
          this.$toastr.error("La génération des lignes de facturation a échouée");
          this.inGenerating = false;
        });
    },
    /**
     * Demande la génération : la facturation est créée « en attente » et générée par le worker
     * Messenger. On en suit l'avancement dans la modale ; fermée, la liste prend le relais.
     */
    validateGeneration() {
      this.inValidating = true;
      this.$http
        .post(process.env.BACK_API_URL + "internalbilling", {
          start_date: this.start_date,
          end_date: this.end_date,
          nb_bill_lines: this.nb_bill_lines,
          total_billing: this.total_billing,
          total_renting: this.total_renting,
          validated: true,
        })
        .then((response) => {
          this.inValidating = false;
          if (response.data.status === "exist") {
            this.$toastr.warning("Une facturation existe déjà pour cette période");
            return;
          }
          this.startGenerationTracking(response.data.generation);
          // Recharge la page courante de la liste (pagination serveur)
          this.$refs.table.refresh();
        })
        .catch((error) => {
          this.$toastr.error("La génération des lignes de facturation n'a pas pu être lancée");
          this.inValidating = false;
        });
    },
    startGenerationTracking(state) {
      this.stopGenerationTracking();
      this.generationId = state.id;
      this.generationRunning = true;
      this.generationPercent = null;
      this.generationSteps = [
        { key: "pending", label: "Mise en file d'attente", state: "active", detail: null },
        { key: "compute", label: "Calcul des lignes de location", state: "pending", detail: null },
        { key: "write", label: "Enregistrement des lignes et rattachement des factures manuelles", state: "pending", detail: null },
      ];
      const startedAt = Date.now();
      this.generationElapsed = 0;
      this.generationClock = setInterval(() => {
        this.generationElapsed = Date.now() - startedAt;
      }, 100);
      if (!this.applyGenerationState(state)) {
        this.pollGeneration(state.id);
      }
    },
    pollGeneration(id) {
      this.generationPollTimer = setTimeout(() => {
        this.generationPollTimer = null;
        this.$http
          .get(process.env.BACK_API_URL + "internalbilling/" + id + "/generation")
          .then((response) => {
            if (id === this.generationId && !this.applyGenerationState(response.data)) {
              this.pollGeneration(id);
            }
          })
          .catch(() => {
            // Erreur réseau passagère : on retente au prochain intervalle
            if (id === this.generationId) {
              this.pollGeneration(id);
            }
          });
      }, GENERATION_POLL_INTERVAL);
    },
    setGenerationStep(key, state, detail) {
      const step = this.generationSteps.find((item) => item.key === key);
      if (step) {
        step.state = state;
        if (detail !== undefined) {
          step.detail = detail;
        }
      }
    },
    /**
     * Reporte l'état serveur sur les étapes.
     *
     * @returns {boolean} la génération est terminée (réussie ou en échec)
     */
    applyGenerationState(state) {
      const current = state.progress_current;
      const total = state.progress_total;
      if (state.status === "done") {
        ["pending", "compute", "write"].forEach((key) => this.setGenerationStep(key, "done"));
        this.setGenerationStep("write", "done", this.formatCount(state.nb_bill_lines) + " lignes");
        this.finishGeneration();
        this.$toastr.success("Facturation générée");
        return true;
      }
      if (state.status === "failed") {
        const active = this.generationSteps.find((step) => step.state === "active");
        if (active) {
          active.state = "error";
          active.detail = state.error;
        }
        this.finishGeneration();
        this.$toastr.error("La génération des lignes de facturation a échoué");
        return true;
      }
      if (state.status === "running") {
        this.setGenerationStep("pending", "done");
        if (state.step === "write") {
          this.setGenerationStep("compute", "done", null);
          this.setGenerationStep("write", "active", total ? this.formatCount(total) + " lignes" : null);
          this.generationPercent = null;
        } else {
          this.setGenerationStep("compute", "active", total ? this.formatCount(current || 0) + " / " + this.formatCount(total) + " mouvements" : null);
          this.generationPercent = total ? Math.min(100, Math.round(((current || 0) / total) * 100)) : null;
        }
      }
      return false;
    },
    finishGeneration() {
      this.stopGenerationTracking();
      this.generationRunning = false;
      this.generationPercent = null;
      this.$refs.table.refresh();
    },
    /** Arrête le suivi (sondage et chronomètre) : génération terminée, ou composant quitté. */
    stopGenerationTracking() {
      clearTimeout(this.generationPollTimer);
      this.generationPollTimer = null;
      clearInterval(this.generationClock);
      this.generationClock = null;
    },
    /**
     * Ouvre la modale d'export pour une facturation et le signale dans l'URL.
     *
     * Sans ce report, la facturation exportée n'existe que dans la mémoire de l'onglet :
     * un rafraîchissement la perd, et le lien envoyé à un collègue ouvre la liste nue.
     * `AdminTableSync` le fait déjà pour l'édition ; l'export s'aligne dessus.
     */
    openExportCsvModal(reference_id) {
      this.exportedBillingId = reference_id;
      // Réinitialisation complète : la modale rouvre toujours sans format, sans filtre ni
      // données de l'export précédent.
      this.exportFormat = null;
      this.exportOnlyBillableFees = false;
      this.resetDataExport();
      this.syncExportToUrl(reference_id);
      this.$root.$emit("bv::show::modal", "billing_lines_export_modal");
    },
    /** Retire le paramètre à la fermeture : l'URL doit décrire ce qui est à l'écran. */
    onModalHidden(bvEvent, modalId) {
      if (modalId === "billing_lines_export_modal") {
        this.exportedBillingId = null;
        this.resetDataExport();
        this.syncExportToUrl(null);
      }
      // La génération se poursuit sur le serveur : la liste, rafraîchie, en suit le statut
      if (modalId === "billing_lines_generation_modal") {
        this.stopGenerationTracking();
      }
    },
    syncExportToUrl(id) {
      const query = { ...this.$route.query };
      if (id) {
        query.export = String(id);
      } else {
        delete query.export;
      }
      // `replace` et non `push` : ouvrir puis fermer la modale ne doit pas empiler deux
      // entrées d'historique, sans quoi « Précédent » la rouvrirait.
      if (JSON.stringify(query) !== JSON.stringify(this.$route.query)) {
        this.$router.replace({ query: query }).catch(() => {});
      }
    },
    /**
     * Lance l'export dans le format choisi : le fichier est produit par le worker Messenger
     * (extraction internal_billing_*, cf. back DataExportController), puis téléchargé.
     */
    loadExportRows() {
      if (!this.exportedBillingId || !this.exportFormat) {
        return;
      }
      this.startDataExport(
        {
          type: {
            legacy: "internal_billing_axiloc",
            sap: "internal_billing_sap",
            charges: "internal_billing_charges",
          }[this.exportFormat],
          internal_billing_id: this.exportedBillingId,
          only_billable_fees: this.exportFormat === "sap" && this.exportOnlyBillableFees,
        },
        {
          legacy: "Extraction des lignes de facturation et des factures manuelles",
          sap: "Extraction et mise au format SAP des lignes de facturation",
          charges: "Calcul de l'encours locatif en fin de période",
        }[this.exportFormat]
      );
    },
  },
};
</script>

<style lang="scss">
@import "~@/assets/scss/_variables.scss";

// Le « danger » du thème est un rose pâle : la suppression prend le rouge franc.
.btn-reset-billing,
.btn-reset-billing:hover,
.btn-reset-billing:focus,
.btn-reset-billing:disabled {
  background-color: $red;
  border-color: $red;
  color: #fff;
}

.btn-reset-billing:hover {
  filter: brightness(0.9);
}

#billing_lines_generation_modal {
  .form-group {
    display: block;
    margin-right: 20px;
  }
}

#billing_lines_export_modal {
  .export-intro {
    color: #676a6c;
    margin-bottom: 16px;
  }

  .export-formats {
    display: block;
    margin-bottom: 24px;
  }

  // Chaque format est une carte entièrement cliquable. C'est un <button> natif : la zone de
  // clic est exactement la carte, sans input flottant susceptible de déborder sur la voisine.
  .export-format {
    display: flex;
    align-items: flex-start;
    width: 100%;
    padding: 12px 14px;
    text-align: left;
    background-color: #fff;
    border: 1px solid #e7eaec;
    border-radius: 3px;
    cursor: pointer;
    transition: border-color 0.15s ease, background-color 0.15s ease;

    & + .export-format {
      margin-top: 8px;
    }

    &:hover {
      border-color: #1ab394;
    }

    &:focus {
      outline: none;
      box-shadow: 0 0 0 2px rgba(26, 179, 148, 0.35);
    }

    &.is-selected {
      border-color: #1ab394;
      background-color: rgba(26, 179, 148, 0.06);

      // Imbriqué ici plutôt qu'en « &.is-selected &__radio » : le parent contenant un ID,
      // cette écriture le répéterait au milieu du sélecteur, qui ne matcherait jamais.
      .export-format__radio {
        border-color: #1ab394;
        box-shadow: inset 0 0 0 4px #1ab394;
      }
    }

    // Indicateur radio dessiné à la main : plus de dépendance au markup de bootstrap-vue.
    &__radio {
      flex: 0 0 auto;
      width: 16px;
      height: 16px;
      margin: 2px 12px 0 0;
      border: 1px solid #cfd5d9;
      border-radius: 50%;
      background-color: #fff;
      transition: border-color 0.15s ease, box-shadow 0.15s ease;
    }

    &__text {
      flex: 1 1 auto;
      min-width: 0;
    }

    &__name {
      display: block;
      font-weight: 600;
      color: #333;
    }

    &__desc {
      display: block;
      margin-top: 2px;
      font-size: 12px;
      line-height: 1.4;
      color: #888;
      white-space: normal;
    }
  }

  // Filtre de mise au point. Il ne porte pas la couleur des formats (le vert de l'application)
  // mais l'ambre des avertissements : ce n'est pas un quatrième format, c'est un outil qui
  // AMPUTE le fichier, et rien à l'écran ne le rappellerait une fois le CSV téléchargé.
  .export-only-fees {
    display: flex;
    align-items: flex-start;
    margin: -12px 0 24px;
    padding: 10px 12px;
    border: 1px dashed #e5b96a;
    border-radius: 3px;
    background-color: rgba(248, 172, 89, 0.09);
    font-weight: normal;
    cursor: pointer;
    transition: border-color 0.15s ease, background-color 0.15s ease;

    &:hover {
      border-color: #f8ac59;
    }

    // Coché, le bloc passe en trait plein et se fonce : le fichier n'est plus l'export
    // complet, et cela doit se voir sans relire la case.
    &.is-active {
      border-style: solid;
      border-color: #f8ac59;
      background-color: rgba(248, 172, 89, 0.18);
    }

    input {
      flex: 0 0 auto;
      margin: 3px 10px 0 0;
    }

    &__text {
      flex: 1 1 auto;
      min-width: 0;
    }

    &__name {
      display: block;
      font-weight: 600;
      color: #8a6321;
    }

    &__badge {
      display: inline-block;
      margin-left: 6px;
      padding: 1px 6px;
      border-radius: 3px;
      background-color: #f8ac59;
      color: #fff;
      font-size: 10px;
      font-weight: 700;
      letter-spacing: 0.06em;
      vertical-align: 1px;
    }

    small {
      display: block;
      margin-top: 2px;
      font-size: 12px;
      line-height: 1.4;
      color: #9a7b46;
    }
  }
}
</style>



// WEBPACK FOOTER //
// src/components/AutomaticBilling/AutomaticBillingList.vue